/**
 * Страница «Отчёты» (см. PRD §8.2, §12.6).
 *
 * Три вкладки: «По времени», «По документам», «По менеджерам».
 * Во всех — auto-load: при заходе на вкладку и изменении фильтров
 * запрос уходит автоматически (для периода — с debounce). Кнопка
 * «Экспорт CSV» одна, справа от h1, и работает с активной вкладкой.
 *
 * Тексты и подсказки заточены под менеджеров по продажам — без
 * жаргона «бакеты / средняя глубина / сессии».
 */

import { useEffect, useMemo, useState } from "react";

import {
  type DocumentReportItem,
  type DocumentsReportResponse,
  type ManagerReportItem,
  type ManagersReportResponse,
  type PeriodGranularity,
  type PeriodReportBucket,
  type PeriodReportResponse,
  analyticsApi,
} from "../api/analytics";
import { humanizeApiError } from "../api/client";
import {
  ANALYTICS_THRESHOLDS_DEFAULTS,
  THRESHOLDS_KEY,
  settingsApi,
} from "../api/settings";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { EmptyTableRow } from "../components/ui/empty-state";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select } from "../components/ui/select";
import { TableSkeleton } from "../components/ui/table-skeleton";
import { cn } from "../lib/utils";

type Tab = "period" | "documents" | "managers";

const TAB_LABELS: Record<Tab, string> = {
  period: "По времени",
  documents: "По документам",
  managers: "По менеджерам",
};

const TAB_HINTS: Record<Tab, string> = {
  period:
    "Динамика отправок и интереса клиентов по дням, неделям или месяцам.",
  documents:
    "Какие коммерческие предложения чаще всего открывают и дочитывают.",
  managers:
    "Активность и эффективность каждого менеджера по продажам.",
};

const FILTER_DEBOUNCE_MS = 400;

// Локализованное число с одним знаком после запятой: «2,5» вместо «2.50».
const decimalFormatter = new Intl.NumberFormat("ru-RU", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function formatAvgPages(value: number): string {
  return decimalFormatter.format(value);
}

// Метка периода для бакета: для дня — дата, для недели/месяца — диапазон,
// чтобы менеджер сразу понимал, какой кусок календаря это покрывает.
function formatBucketRange(
  bucketStartIso: string,
  granularity: PeriodGranularity,
): string {
  const start = new Date(bucketStartIso);
  if (Number.isNaN(start.getTime())) return bucketStartIso.slice(0, 10);

  if (granularity === "day") {
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(start);
  }

  if (granularity === "month") {
    return new Intl.DateTimeFormat("ru-RU", {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).format(start);
  }

  // week — показываем диапазон «01–07 мая 2026»
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  const dayFmt = new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    timeZone: "UTC",
  });
  const tailFmt = new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
  return `${dayFmt.format(start)}–${tailFmt.format(end)}`;
}

function extractMessage(err: unknown): string {
  return humanizeApiError(err);
}

function isoStartOfDayUtc(yyyyMmDd: string): string {
  return new Date(`${yyyyMmDd}T00:00:00Z`).toISOString();
}

function isoStartOfNextDayUtc(yyyyMmDd: string): string {
  const d = new Date(`${yyyyMmDd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString();
}

function todayUtc(offsetDays = 0): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function csvFilename(kind: string): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${kind}_${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}.csv`;
}

// Подтягиваем порог «горячего лида» из общих настроек, чтобы показать его
// рядом с метрикой. Если запрос упадёт — молча используем дефолт из PRD §7.4.
function useHotLeadThreshold(): number {
  const [threshold, setThreshold] = useState<number>(
    ANALYTICS_THRESHOLDS_DEFAULTS.notification_threshold_pages,
  );
  useEffect(() => {
    let cancelled = false;
    settingsApi
      .get()
      .then((res) => {
        if (cancelled) return;
        const stored = res.settings[THRESHOLDS_KEY];
        if (stored && typeof stored === "object" && !Array.isArray(stored)) {
          const value = (stored as Record<string, unknown>)
            .notification_threshold_pages;
          if (typeof value === "number") {
            setThreshold(value);
          }
        }
      })
      .catch(() => {
        // Молча оставляем дефолт.
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return threshold;
}

export function ReportsPage() {
  const [tab, setTab] = useState<Tab>("period");
  const hotLeadThreshold = useHotLeadThreshold();

  // Период живёт на уровне ReportsPage, чтобы кнопка «Экспорт CSV» наверху
  // знала, какой диапазон/гранулярность использовать.
  const [dateFrom, setDateFrom] = useState<string>(() => todayUtc(-30));
  const [dateTo, setDateTo] = useState<string>(() => todayUtc(0));
  const [granularity, setGranularity] = useState<PeriodGranularity>("day");
  const invalidRange = dateFrom >= dateTo;

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  async function exportCsv() {
    setExporting(true);
    setExportError(null);
    try {
      if (tab === "period") {
        if (invalidRange) {
          setExportError("Выберите дату «По» позже даты «С».");
          return;
        }
        await analyticsApi.exportPeriodCsv(
          {
            date_from: isoStartOfDayUtc(dateFrom),
            date_to: isoStartOfNextDayUtc(dateTo),
            granularity,
          },
          csvFilename("period"),
        );
      } else if (tab === "documents") {
        await analyticsApi.exportDocumentsCsv(csvFilename("documents"));
      } else {
        await analyticsApi.exportManagersCsv(csvFilename("managers"));
      }
    } catch (err) {
      setExportError(extractMessage(err));
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold">Отчёты</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Кому и какие коммерческие предложения вы отправляли, как часто их
            открывают и сколько страниц дочитывают. Цифры обновляются
            автоматически.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <Button
            variant="outline"
            onClick={() => void exportCsv()}
            disabled={exporting || (tab === "period" && invalidRange)}
            title={`Скачать CSV для вкладки «${TAB_LABELS[tab]}»`}
          >
            {exporting ? "Готовим файл…" : "Скачать CSV"}
          </Button>
          <span className="text-xs text-muted-foreground">
            из вкладки «{TAB_LABELS[tab]}»
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b" role="tablist">
        {(["period", "documents", "managers"] as const).map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={cn(
              "px-3 py-2 text-sm border-b-2 -mb-px",
              tab === t
                ? "border-foreground text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      <p className="text-sm text-muted-foreground">{TAB_HINTS[tab]}</p>

      {exportError && (
        <Card>
          <CardContent className="text-red-700 text-sm py-3">{exportError}</CardContent>
        </Card>
      )}

      {tab === "period" && (
        <PeriodSection
          dateFrom={dateFrom}
          dateTo={dateTo}
          granularity={granularity}
          invalidRange={invalidRange}
          hotLeadThreshold={hotLeadThreshold}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onGranularityChange={setGranularity}
        />
      )}
      {tab === "documents" && (
        <DocumentsSection hotLeadThreshold={hotLeadThreshold} />
      )}
      {tab === "managers" && (
        <ManagersSection hotLeadThreshold={hotLeadThreshold} />
      )}
    </div>
  );
}

interface PeriodSectionProps {
  dateFrom: string;
  dateTo: string;
  granularity: PeriodGranularity;
  invalidRange: boolean;
  hotLeadThreshold: number;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
  onGranularityChange: (v: PeriodGranularity) => void;
}

function PeriodSection({
  dateFrom,
  dateTo,
  granularity,
  invalidRange,
  hotLeadThreshold,
  onDateFromChange,
  onDateToChange,
  onGranularityChange,
}: PeriodSectionProps) {
  const [report, setReport] = useState<PeriodReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounce — чтобы при наборе даты в инпуте не отправлять запрос на
  // каждое нажатие. Гранулярность через select меняется одним кликом —
  // дополнительной задержки не нужно, но один таймер на все три фильтра
  // проще, чем разные.
  const filterKey = useMemo(
    () => `${dateFrom}|${dateTo}|${granularity}`,
    [dateFrom, dateTo, granularity],
  );
  const [appliedFilterKey, setAppliedFilterKey] = useState(filterKey);
  useEffect(() => {
    const t = window.setTimeout(() => setAppliedFilterKey(filterKey), FILTER_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [filterKey]);

  useEffect(() => {
    if (invalidRange) {
      setError("Выберите дату «По» позже даты «С».");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    analyticsApi
      .periodReport({
        date_from: isoStartOfDayUtc(dateFrom),
        date_to: isoStartOfNextDayUtc(dateTo),
        granularity,
      })
      .then((r) => {
        if (cancelled) return;
        setReport(r);
      })
      .catch((err) => {
        if (!cancelled) setError(extractMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilterKey, invalidRange]);

  return (
    <div className="space-y-4" role="tabpanel">
      <div className="neu-canvas neu-raised rounded-2xl p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr] gap-3 items-end">
          <div>
            <Label htmlFor="period-from" className="text-xs uppercase tracking-wider text-muted-foreground">С</Label>
            <Input
              id="period-from"
              type="date"
              value={dateFrom}
              onChange={(e) => onDateFromChange(e.target.value)}
              className="neu-inset border-transparent shadow-none h-10"
            />
          </div>
          <div>
            <Label htmlFor="period-to" className="text-xs uppercase tracking-wider text-muted-foreground">По</Label>
            <Input
              id="period-to"
              type="date"
              value={dateTo}
              onChange={(e) => onDateToChange(e.target.value)}
              className="neu-inset border-transparent shadow-none h-10"
            />
          </div>
          <div>
            <Label htmlFor="period-granularity" className="text-xs uppercase tracking-wider text-muted-foreground">Группировка</Label>
            <Select
              id="period-granularity"
              value={granularity}
              onChange={(e) =>
                onGranularityChange(e.target.value as PeriodGranularity)
              }
              className="neu-inset border-transparent shadow-none h-10"
            >
              <option value="day">По дням</option>
              <option value="week">По неделям</option>
              <option value="month">По месяцам</option>
            </Select>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Дата «По» включительно. Период действует только на этой вкладке —
          на «По документам» и «По менеджерам» показаны данные за всё время.
        </p>
      </div>

      <PeriodSummary
        buckets={report?.buckets ?? []}
        loading={loading}
      />

      <PeriodSparkline
        buckets={report?.buckets ?? []}
        granularity={granularity}
        loading={loading}
      />

      {error && (
        <Card>
          <CardContent className="text-red-700 text-sm py-3">{error}</CardContent>
        </Card>
      )}

      <PeriodTable
        buckets={report?.buckets ?? []}
        granularity={granularity}
        loading={loading}
        hotLeadThreshold={hotLeadThreshold}
      />
    </div>
  );
}

function PeriodSummary({
  buckets,
  loading,
}: {
  buckets: PeriodReportBucket[];
  loading: boolean;
}) {
  const totals = useMemo(() => {
    return buckets.reduce(
      (acc, b) => ({
        dispatches: acc.dispatches + b.dispatches_created,
        sessions: acc.sessions + b.sessions_started,
        hot: acc.hot + b.hot_leads,
        depthSum: acc.depthSum + b.avg_depth * b.sessions_started,
      }),
      { dispatches: 0, sessions: 0, hot: 0, depthSum: 0 },
    );
  }, [buckets]);
  const avgDepth =
    totals.sessions > 0 ? totals.depthSum / totals.sessions : 0;

  const dim = loading && buckets.length === 0;
  return (
    <div
      className={cn(
        "neu-canvas rounded-2xl p-3 grid grid-cols-2 md:grid-cols-4 gap-3 transition-opacity",
        dim && "opacity-50",
      )}
      aria-busy={dim || undefined}
    >
      <KpiTile label="Отправлено КП" value={totals.dispatches} />
      <KpiTile label="Открытий" value={totals.sessions} />
      <KpiTile label="Горячих лидов" value={totals.hot} tone="accent" />
      <KpiTile
        label="Прочитано (среднее)"
        value={formatAvgPages(avgDepth)}
      />
    </div>
  );
}

function KpiTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone?: "accent";
}) {
  return (
    <div className="neu-raised rounded-xl p-4">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-1 font-mono text-2xl font-bold tabular-nums",
          tone === "accent" ? "text-primary" : "text-foreground",
        )}
      >
        {value}
      </div>
    </div>
  );
}

// Inline SVG-спарклайн без внешних chart-зависимостей. Показывает
// динамику «Открытий» (sessions_started) по бакетам — это самая
// «трендовая» метрика среди четырёх KPI: горячие лиды редки, отправок
// тоже мало, а сессии хорошо отражают «жив ли поток просмотров».
// Дизайн намеренно тихий: одна линия + точки, без сетки и осей —
// числа уже есть в таблице ниже.
function PeriodSparkline({
  buckets,
  granularity,
  loading,
}: {
  buckets: PeriodReportBucket[];
  granularity: PeriodGranularity;
  loading: boolean;
}) {
  // Меньше двух точек — рисовать линию бессмысленно, скрываем блок.
  if (buckets.length < 2) return null;

  const values = buckets.map((b) => b.sessions_started);
  const maxVal = Math.max(1, ...values);
  const w = 600;
  const h = 80;
  const padX = 4;
  const padY = 8;
  const innerW = w - padX * 2;
  const innerH = h - padY * 2;
  const stepX = innerW / (values.length - 1);

  const points = values.map((v, i) => {
    const x = padX + stepX * i;
    const y = padY + innerH * (1 - v / maxVal);
    return { x, y, v };
  });
  const path = points
    .map((p, i) => (i === 0 ? `M ${p.x},${p.y}` : `L ${p.x},${p.y}`))
    .join(" ");
  const area =
    `M ${points[0]!.x},${padY + innerH} ` +
    points.map((p) => `L ${p.x},${p.y}`).join(" ") +
    ` L ${points[points.length - 1]!.x},${padY + innerH} Z`;

  const startLabel = formatBucketRange(buckets[0]!.bucket_start, granularity);
  const endLabel = formatBucketRange(
    buckets[buckets.length - 1]!.bucket_start,
    granularity,
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-baseline justify-between gap-2">
        <CardTitle className="text-base font-medium">
          Открытия — динамика
        </CardTitle>
        <span className="text-xs text-muted-foreground">
          максимум за период: {maxVal}
        </span>
      </CardHeader>
      <CardContent className={cn("pt-0", loading && "opacity-50")}>
        <svg
          viewBox={`0 0 ${w} ${h}`}
          preserveAspectRatio="none"
          className="w-full h-20"
          role="img"
          aria-label={`График открытий по ${buckets.length} периодам`}
          data-testid="reports-sparkline"
        >
          <path d={area} fill="hsl(var(--accent-warm) / 0.18)" />
          <path
            d={path}
            fill="none"
            stroke="hsl(var(--accent-warm))"
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {points.map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r="2"
              fill="hsl(var(--accent-warm))"
            >
              <title>{`${formatBucketRange(buckets[i]!.bucket_start, granularity)}: ${p.v}`}</title>
            </circle>
          ))}
        </svg>
        <div className="mt-1 flex justify-between text-[11px] text-muted-foreground tabular-nums">
          <span>{startLabel}</span>
          <span>{endLabel}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function PeriodTable({
  buckets,
  granularity,
  loading,
  hotLeadThreshold,
}: {
  buckets: PeriodReportBucket[];
  granularity: PeriodGranularity;
  loading: boolean;
  hotLeadThreshold: number;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-medium">По периодам</CardTitle>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground">
            <tr className="border-b">
              <th
                className="text-left font-normal px-4 py-2"
                title="Календарный отрезок, к которому относится строка"
              >
                Период
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title="Сколько ссылок на КП было создано и отправлено за этот период"
              >
                Отправлено КП
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title="Сколько раз клиенты открывали документ. Один клиент может открыть несколько раз — это считается разными открытиями"
              >
                Открытий
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title={`Открытия, в которых клиент дочитал документ до уровня заинтересованности. Сейчас порог — от ${hotLeadThreshold} прочитанных страниц (меняется в Настройках → Аналитика)`}
              >
                Горячих лидов
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title="Сколько страниц в среднем читают клиенты за одно открытие документа"
              >
                Прочитано страниц (в среднем)
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && buckets.length === 0 ? (
              <TableSkeleton columns={5} />
            ) : buckets.length === 0 ? (
              <EmptyTableRow colSpan={5}>
                За этот период данных пока нет. Попробуйте расширить диапазон
                или отправьте новое КП.
              </EmptyTableRow>
            ) : (
              buckets.map((b) => (
                <tr key={b.bucket_start} className="border-b last:border-b-0">
                  <td className="px-4 py-2">
                    {formatBucketRange(b.bucket_start, granularity)}
                  </td>
                  <td className="px-4 py-2">{b.dispatches_created}</td>
                  <td className="px-4 py-2">{b.sessions_started}</td>
                  <td className="px-4 py-2">{b.hot_leads}</td>
                  <td className="px-4 py-2">{formatAvgPages(b.avg_depth)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function DocumentsSection({
  hotLeadThreshold,
}: {
  hotLeadThreshold: number;
}) {
  const [report, setReport] = useState<DocumentsReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    analyticsApi
      .documentsReport()
      .then((r) => setReport(r))
      .catch((err) => setError(extractMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4" role="tabpanel">
      {error && (
        <Card>
          <CardContent className="text-red-700 text-sm py-3">{error}</CardContent>
        </Card>
      )}
      <DocumentsTable
        items={report?.items ?? []}
        loading={loading}
        hotLeadThreshold={hotLeadThreshold}
      />
    </div>
  );
}

function DocumentsTable({
  items,
  loading,
  hotLeadThreshold,
}: {
  items: DocumentReportItem[];
  loading: boolean;
  hotLeadThreshold: number;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-medium">По документам</CardTitle>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground">
            <tr className="border-b">
              <th
                className="text-left font-normal px-4 py-2"
                title="Название документа в библиотеке"
              >
                Документ
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title="Сколько раз этот документ был отправлен клиентам (за всё время)"
              >
                Отправлено
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title="Сколько раз клиенты открывали этот документ. Один клиент может открыть несколько раз — это считается разными открытиями"
              >
                Открытий
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title="Сколько страниц всего изучили клиенты по этому документу. Страница считается прочитанной, если клиент задержался на ней достаточно долго"
              >
                Прочитано страниц
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title="Сколько страниц в среднем читают клиенты за одно открытие документа"
              >
                Прочитано страниц (в среднем)
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title={`Открытия, в которых клиент дочитал документ до уровня заинтересованности. Сейчас порог — от ${hotLeadThreshold} прочитанных страниц (меняется в Настройках → Аналитика)`}
              >
                Горячих лидов
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <TableSkeleton columns={6} />
            ) : items.length === 0 ? (
              <EmptyTableRow colSpan={6}>
                Пока нет ни одного отправленного документа. Загрузите КП в
                библиотеку и отправьте первому клиенту.
              </EmptyTableRow>
            ) : (
              items.map((d) => (
                <tr key={d.document_id} className="border-b last:border-b-0">
                  <td className="px-4 py-2 font-medium">{d.title}</td>
                  <td className="px-4 py-2">{d.total_dispatches}</td>
                  <td className="px-4 py-2">{d.total_sessions}</td>
                  <td className="px-4 py-2">{d.total_counted_pages}</td>
                  <td className="px-4 py-2">{formatAvgPages(d.avg_depth)}</td>
                  <td className="px-4 py-2">{d.hot_leads}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function ManagersSection({
  hotLeadThreshold,
}: {
  hotLeadThreshold: number;
}) {
  const [report, setReport] = useState<ManagersReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    analyticsApi
      .managersReport()
      .then((r) => setReport(r))
      .catch((err) => setError(extractMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4" role="tabpanel">
      {error && (
        <Card>
          <CardContent className="text-red-700 text-sm py-3">{error}</CardContent>
        </Card>
      )}
      <ManagersTable
        items={report?.items ?? []}
        loading={loading}
        hotLeadThreshold={hotLeadThreshold}
      />
    </div>
  );
}

function ManagersTable({
  items,
  loading,
  hotLeadThreshold,
}: {
  items: ManagerReportItem[];
  loading: boolean;
  hotLeadThreshold: number;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-medium">По менеджерам</CardTitle>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground">
            <tr className="border-b">
              <th
                className="text-left font-normal px-4 py-2"
                title="Имя сотрудника, который отправлял КП"
              >
                Менеджер
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title="Корпоративный e-mail сотрудника"
              >
                E-mail
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title="Сколько ссылок на КП этот менеджер отправил клиентам (за всё время)"
              >
                Отправлено
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title="Сколько раз клиенты открывали документы этого менеджера. Один клиент может открыть несколько раз — это считается разными открытиями"
              >
                Открытий
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title="Сколько страниц в среднем читают клиенты этого менеджера за одно открытие"
              >
                Прочитано страниц (в среднем)
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title={`Открытия, в которых клиент дочитал документ до уровня заинтересованности. Сейчас порог — от ${hotLeadThreshold} прочитанных страниц (меняется в Настройках → Аналитика)`}
              >
                Горячих лидов
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <TableSkeleton columns={6} />
            ) : items.length === 0 ? (
              <EmptyTableRow colSpan={6}>
                Пока никто из менеджеров не отправлял КП через систему.
              </EmptyTableRow>
            ) : (
              items.map((m) => (
                <tr key={m.manager_user_id} className="border-b last:border-b-0">
                  <td className="px-4 py-2 font-medium">{m.full_name}</td>
                  <td className="px-4 py-2 text-muted-foreground">{m.email}</td>
                  <td className="px-4 py-2">{m.total_dispatches}</td>
                  <td className="px-4 py-2">{m.total_sessions}</td>
                  <td className="px-4 py-2">{formatAvgPages(m.avg_depth)}</td>
                  <td className="px-4 py-2">{m.hot_leads}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
